'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface AudioItem {
  file: File;
  url: string;
  id: string;
}

export default function AudioMergerTool() {
  const [files, setFiles] = useState<AudioItem[]>([]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav'>('mp3');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const arr = Array.from(newFiles);
    setError(null);
    setResultUrl(null);
    const items: AudioItem[] = arr.map(f => ({
      file: f,
      url: URL.createObjectURL(f),
      id: `${f.name}-${Date.now()}-${Math.random()}`,
    }));
    setFiles(prev => [...prev, ...items]);
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files);
  };

  const handleDragSort = () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    const newFiles = [...files];
    const dragged = newFiles.splice(dragItem.current, 1)[0];
    newFiles.splice(dragOverItem.current, 0, dragged);
    setFiles(newFiles);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id));
    setResultUrl(null);
  };

  const handleMerge = async () => {
    if (files.length < 2) {
      setError('Please add at least 2 audio files to merge.');
      return;
    }
    setProcessing(true);
    setProgress(0);
    setError(null);
    setResultUrl(null);

    try {
      setProgressLabel('Loading FFmpeg engine…');
      setProgress(10);

      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();

      ffmpeg.on('progress', ({ progress: p }) => {
        setProgress(Math.round(20 + p * 70));
        setProgressLabel(`Merging… ${Math.round(p * 100)}%`);
      });

      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });

      setProgress(20);
      setProgressLabel('Reading files…');

      // Write all input files
      const inputNames: string[] = [];
      for (let i = 0; i < files.length; i++) {
        const ext = files[i].file.name.split('.').pop() || 'mp3';
        const name = `input_${i}.${ext}`;
        await ffmpeg.writeFile(name, await fetchFile(files[i].file));
        inputNames.push(name);
      }

      // Create concat list
      const concatContent = inputNames.map(n => `file '${n}'`).join('\n');
      await ffmpeg.writeFile('concat.txt', concatContent);

      const outputName = `merged.${outputFormat}`;
      await ffmpeg.exec([
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat.txt',
        '-c', outputFormat === 'mp3' ? 'libmp3lame' : 'pcm_s16le',
        outputName,
      ]);

      setProgress(95);
      setProgressLabel('Preparing download…');

      const data = await ffmpeg.readFile(outputName);
      const blob = new Blob([data], { type: outputFormat === 'mp3' ? 'audio/mpeg' : 'audio/wav' });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgress(100);
      setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Merging failed. Make sure all files are valid audio files in compatible formats.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `merged_audio.${outputFormat}`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div
        className={`rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer transition-colors ${
          isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'
        }`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <span className="text-3xl block mb-2">🔗</span>
        <p className="font-semibold text-gray-800 mb-1">Add Audio Files</p>
        <p className="text-gray-500 text-sm">Drag & drop or click to browse · Add multiple files</p>
        <input ref={fileInputRef} type="file" accept=".mp3,.wav,.ogg,.aac,.flac,.m4a" multiple className="hidden" onChange={(e) => { if (e.target.files) addFiles(e.target.files); }} />
      </div>

      {files.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Files — drag to reorder</p>
          {files.map((item, i) => (
            <div
              key={item.id}
              draggable
              onDragStart={() => { dragItem.current = i; }}
              onDragEnter={() => { dragOverItem.current = i; }}
              onDragEnd={handleDragSort}
              onDragOver={(e) => e.preventDefault()}
              className="flex items-center gap-3 bg-white border border-gray-100 rounded-xl p-3 cursor-grab active:cursor-grabbing"
            >
              <span className="text-gray-300 text-lg select-none">⠿</span>
              <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <span className="text-amber-700 text-xs font-bold">{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{item.file.name}</p>
                <p className="text-xs text-gray-400">{formatBytes(item.file.size)}</p>
              </div>
              <button onClick={() => removeFile(item.id)} className="text-gray-300 hover:text-red-400 transition-colors p-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Output Format</p>
          <div className="flex gap-3">
            {(['mp3', 'wav'] as const).map(fmt => (
              <button
                key={fmt}
                onClick={() => setOutputFormat(fmt)}
                className={`px-5 py-2 rounded-xl text-sm font-semibold border transition-colors ${
                  outputFormat === fmt ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'
                }`}
              >
                {fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      {processing && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>{progressLabel}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {resultUrl && (
        <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-semibold text-green-800 text-sm">Merged successfully! · {formatBytes(resultSize)}</p>
          </div>
          <audio src={resultUrl} controls className="w-full h-10" />
          <button onClick={handleDownload} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm">
            ⬇️ Download Merged Audio
          </button>
        </div>
      )}

      {files.length >= 2 && !resultUrl && (
        <button
          onClick={handleMerge}
          disabled={processing}
          className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
        >
          {processing ? 'Merging…' : `Merge ${files.length} Files`}
        </button>
      )}
    </div>
  );
}
